import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { FiClock } from 'react-icons/fi';
import { TextareaAutosize } from '@/components/ui/textarea-autosize';
import { Loader } from '@/components/custom/loader';
import promiseRequest from '@/utils/promiseToast';
import { DashboardContent } from '@/layouts/dashboard';
import { TiaBanner } from '@/components/custom/tia-banner';
import { PlayfulCardButton } from '@/components/custom/playful-card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FormField } from '@/components/custom/form';
import { InputFile } from '@/components/ui/input';
import { ACTIVITY_BADGE_CLASSNAME, OUTLINE_MISSION_BUTTON_CLASSNAME, activityStatusVariant } from '../../activityStatusVariant';
import tiaAtividades from '@/assets/images/tia/TIA-ATIVIDADES.png';

const PLAYFUL_TAB_CLASSNAME =
  'flex-1 px-4 py-1.5 text-sm data-[state=active]:bg-[var(--color-mission)] data-[state=active]:text-white data-[state=active]:font-extrabold data-[state=active]:shadow-[0_3px_0_var(--color-mission-dark)] data-[state=active]:-translate-y-px';

export default function ComplementaryActivityDetailsPlayful(props) {
  const navigate = useNavigate();
  const { uri } = props;

  const { t } = useTranslation();
  const client = useClient();
  const { promiseToast, isSubmitting } = promiseRequest();
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [activity, setActivity] = useState();
  const [fileType, setFileType] = useState(t('activity.toggleFile'));

  useEffect(() => {
    getActivity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getActivity = async () => {
    try {
      setIsLoading(true);
      const { data } = await client.fetch(['activities', props.id]);
      setActivity(data);
    } catch (e) {
      console.error(e);
      toast.error(t('toast.errorGetActivity'));
    } finally {
      setIsLoading(false);
    }
  };

  const form = useFormik({
    initialValues: { file: null, url: null, comment: '' },
    onSubmit: () => setShowModal(true)
  });

  const toBase64 = file =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });

  const submitForm = async () => {
    if (isSubmitting) return;

    const { values } = form;
    const parsedForm = {
      ...values,
      file: undefined,
      url: undefined,
      comment: undefined,
      'comments-attributes': [{ body: values?.comment }],
      'activity-file-attributes': { url: null, file: null }
    };

    if (values.file && values.file !== '') {
      parsedForm['activity-file-attributes'].file = await toBase64(values.file);
    }

    if (values.url && values.url !== '') {
      parsedForm['activity-file-attributes'].url = values.url;
    }

    promiseToast({
      url: `activities/${props.id}/activity_students`,
      request: parsedForm,
      onSuccess: () => {
        setShowModal(false);
        navigate(-1);
      },
      successText: t('toast.successSubmitActivity'),
      errorText: t('toast.errorSubmitActivity')
    });
  };

  const breadcrumbs = {
    title: 'Atividades',
    nav: [
      {
        route: '/atividade-complementar',
        name: 'Atividade Complementar',
        isActive: false
      },
      {
        route: uri,
        name: activity?.title,
        isActive: true
      }
    ]
  };

  if (isLoading) {
    return (
      <DashboardContent
        variant="plain"
        breadcrumbs={breadcrumbs}
      >
        <Loader />
      </DashboardContent>
    );
  }

  const status = activity['activity-status-or-submitted-count'];
  const isRequired = activity['learning-system-activities']?.[0]?.is_required;

  return (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadcrumbs}
    >
      <TiaBanner
        className="mb-6 sm:mt-20"
        image={tiaAtividades}
        imageOffset={210}
        text="Chegou a hora de mandar sua atividade! Anexe um arquivo ou cole o link, conte um pouco sobre o que você fez e é só enviar. Boa sorte! ✨"
      />

      <div className="rounded-2xl border border-neutral-lightest bg-white p-6 shadow-card">
        <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="mb-2 flex flex-wrap items-center gap-1.5">
              {isRequired && (
                <Badge
                  variant="danger"
                  className={ACTIVITY_BADGE_CLASSNAME}
                >
                  Obrigatória
                </Badge>
              )}
              {status && (
                <Badge
                  variant={activityStatusVariant(status, t)}
                  className={ACTIVITY_BADGE_CLASSNAME}
                >
                  {status}
                </Badge>
              )}
            </div>

            <h1
              className="mb-1 text-xl font-extrabold"
              style={{ color: '#222731' }}
            >
              {activity.title}
            </h1>

            <div className="flex items-center gap-1.5 text-sm font-semibold text-neutral-500">
              <FiClock className="h-4 w-4 text-[var(--color-mission-medium)]" />
              {t('textsCommon.module')} {activity.modulus} | {t('textsCommon.unit')} {activity.unit} | {activity['class-hours'] < 2 ? `${activity['class-hours']} ${t('tasks.hour')}` : `${activity['class-hours']} ${t('tasks.hour')}`}
            </div>
          </div>

          {activity['activity-file'] && (
            <a
              href={activity['activity-file']?.url || activity['activity-file']?.file}
              target="_blank"
              rel="noopener noreferrer"
              className={OUTLINE_MISSION_BUTTON_CLASSNAME}
            >
              {t('button.supportMaterial')}
            </a>
          )}
        </div>

        <p className="mb-6 text-base text-neutral-dark">{activity.description}</p>

        <form
          method="post"
          onSubmit={form.handleSubmit}
          className="space-y-5"
        >
          <Tabs
            value={fileType}
            onValueChange={value => {
              if (value === t('activity.toggleFile')) {
                form.setFieldValue('url', null);
              } else {
                form.setFieldValue('file', null);
              }
              setFileType(value);
            }}
          >
            <TabsList className="w-full max-w-xs justify-between gap-1 bg-neutral-bright p-1 md:w-max">
              <TabsTrigger
                value={t('activity.toggleFile')}
                className={PLAYFUL_TAB_CLASSNAME}
              >
                {t('activity.toggleFile')}
              </TabsTrigger>
              <TabsTrigger
                value="URL"
                className={PLAYFUL_TAB_CLASSNAME}
              >
                URL
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {fileType === t('activity.toggleFile') && (
            <InputFile
              file={form.values.file}
              name="file"
              id="file"
              onChange={e => form.setFieldValue('file', e.target.files[0])}
            />
          )}

          {fileType === 'URL' && (
            <FormField
              id="url"
              name="url"
              type="url"
              aria-label={t('activity.placeholderSentUrl')}
              placeholder={t('activity.placeholderSentUrl')}
              onChange={form.handleChange}
              defaultValue={form.values.url}
            />
          )}

          <FormField
            label="*Realize um comentário para enviar com sucesso"
            id="comment"
          >
            <TextareaAutosize
              id="comment"
              name="comment"
              placeholder={t('activity.placeholderWriteComment')}
              onChange={form.handleChange}
              defaultValue={form.values.comment}
            />
          </FormField>

          <PlayfulCardButton
            type="submit"
            className="w-full md:w-auto md:px-10"
          >
            {t('button.submit')}
          </PlayfulCardButton>
        </form>
      </div>

      <Dialog
        open={showModal}
        onOpenChange={setShowModal}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('activity.warningSubmit')}</DialogTitle>
          </DialogHeader>

          <div className="flex items-center justify-center gap-3">
            <Button
              variant="outline"
              wide
              onClick={() => setShowModal(false)}
              type="button"
            >
              {t('button.cancel')}
            </Button>
            <PlayfulCardButton
              onClick={() => submitForm()}
              disabled={isSubmitting}
              className="flex-1"
            >
              {t('button.submit')}
            </PlayfulCardButton>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardContent>
  );
}
